import Guacamole, { type Client, type Keyboard, type Tunnel } from 'guacamole-common-js'

type ActiveSession = {
  client: Client
  tunnel: Tunnel
  keyboard: Keyboard | null
}

let active: ActiveSession | null = null
const tunnelXhrs = new Set<XMLHttpRequest>()
let xhrPatchCount = 0
let nativeOpen: typeof XMLHttpRequest.prototype.open | null = null

function isTunnelUrl(url: string | URL): boolean {
  const s = String(url)
  return s.includes('/api/guacamole-tunnel-http') || s.includes('guacamole-tunnel')
}

function patchXhrTracking() {
  if (xhrPatchCount === 0) {
    nativeOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      if (isTunnelUrl(url)) {
        tunnelXhrs.add(this)
        const drop = () => tunnelXhrs.delete(this)
        this.addEventListener('loadend', drop)
        this.addEventListener('abort', drop)
        this.addEventListener('error', drop)
      }
      return nativeOpen!.call(
        this,
        method,
        url,
        async ?? true,
        username,
        password,
      )
    } as typeof XMLHttpRequest.prototype.open
  }
  xhrPatchCount += 1
}

function unpatchXhrTracking() {
  xhrPatchCount = Math.max(0, xhrPatchCount - 1)
  if (xhrPatchCount === 0 && nativeOpen) {
    XMLHttpRequest.prototype.open = nativeOpen
    nativeOpen = null
  }
}

function abortTunnelXhrs() {
  for (const xhr of [...tunnelXhrs]) {
    try {
      xhr.onreadystatechange = null
      xhr.abort()
    } catch {
      /* ignore */
    }
    tunnelXhrs.delete(xhr)
  }
}

function releaseKeyboard(keyboard: Keyboard | null) {
  if (!keyboard) return
  try {
    keyboard.onkeydown = null
    keyboard.onkeyup = null
    keyboard.reset()
  } catch {
    /* ignore */
  }
}

/** Register the live Guacamole client/tunnel (one at a time). */
export function bindGuacamoleSession(session: ActiveSession) {
  // Kill any leftover session first
  disposeGuacamoleSession()
  active = session
  patchXhrTracking()
}

export function setGuacamoleKeyboard(keyboard: Keyboard | null) {
  if (active) active.keyboard = keyboard
}

/** Hard stop: silence handlers, disconnect client/tunnel, abort pending XHR. */
export function disposeGuacamoleSession() {
  const cur = active
  active = null
  if (!cur) {
    abortTunnelXhrs()
    return
  }

  releaseKeyboard(cur.keyboard)
  cur.keyboard = null

  // Silence callbacks so late packets cannot re-schedule keep-alive / reconnect
  try {
    cur.tunnel.oninstruction = null
  } catch {
    /* ignore */
  }
  try {
    cur.tunnel.onerror = null
  } catch {
    /* ignore */
  }
  try {
    cur.tunnel.onstatechange = null
  } catch {
    /* ignore */
  }
  try {
    cur.client.onstatechange = null
  } catch {
    /* ignore */
  }
  try {
    cur.client.onerror = null
  } catch {
    /* ignore */
  }
  try {
    cur.client.onclipboard = null
  } catch {
    /* ignore */
  }

  try {
    cur.client.disconnect()
  } catch {
    /* ignore */
  }
  try {
    cur.tunnel.disconnect()
  } catch {
    /* ignore */
  }

  // Force CLOSED if library left it open
  try {
    const t = cur.tunnel as Tunnel & { setState?: (s: number) => void; state?: number }
    const closed = (Guacamole as { Tunnel?: { State?: { CLOSED?: number } } }).Tunnel?.State?.CLOSED
    if (typeof closed === 'number' && t.state !== closed && typeof t.setState === 'function') {
      t.setState(closed)
    }
  } catch {
    /* ignore */
  }

  abortTunnelXhrs()
  unpatchXhrTracking()
}

export function hasActiveGuacamoleSession() {
  return active != null
}
